/**
 * LOUD/QUIET Checkbox — square, 2px ink border, Voltage-yellow checked fill with a heavy tick.
 * Pairs with a short, human label.
 *
 * Ported 1:1 from the compiled `_ds_bundle.js`. The control is a real <input type="checkbox">
 * with `appearance: none`, absolutely positioned over a 22px box; the tick is a sibling span
 * revealed by an adjacent-sibling CSS rule. Because `appearance: none` cannot be undone by
 * inline styles, that one rule ships as an inline <style> element — the same technique the
 * bundle uses, so the component stays free of any stylesheet dependency.
 *
 * Non-obvious decisions:
 * - The bundle's inline CSS is UNSCOPED (`input:checked{background:var(--lq-yellow)!important}`),
 *   which repaints every checked checkbox anywhere on the page. Scoped here to
 *   `[data-lq-checkbox]`. Identical rendering for this component; no longer a global leak.
 * - The tick keeps `opacity` toggling rather than mount/unmount so there is no layout shift.
 * - `style` lands on the outer <label>; `...rest` lands on the <input>.
 */
import * as React from 'react';

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'style' | 'type' | 'children'> {
  /** Short, human label. */
  label?: string;
  /** @default false */
  disabled?: boolean;
  /** Applied to the outer <label> row, not the box. */
  style?: React.CSSProperties;
}

// The tick's visibility and the checked fill are the only two things inline styles cannot express.
const CHECKBOX_CSS =
  '[data-lq-checkbox] [data-lq-tick]{opacity:0}' +
  '[data-lq-checkbox] input:checked + [data-lq-tick]{opacity:1}' +
  '[data-lq-checkbox] input:checked{background:var(--lq-yellow)!important}';

/** `:focus-visible` is unsupported on a few old engines; fall back to always showing the ring. */
function isFocusVisible(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

export function Checkbox({
  label,
  id,
  checked,
  defaultChecked,
  onChange,
  disabled = false,
  style,
  onFocus,
  onBlur,
  ...rest
}: CheckboxProps) {
  // The bundle derives the id from the label text, so two checkboxes labelled the same way get
  // the same id and both <label>s drive the first one. useId is per-instance, so it cannot
  // collide; a caller-supplied `id` still wins.
  const autoId = React.useId();
  const boxId = id || `lq-cb-${autoId}`;

  // NOTE: the bundle gives the checkbox no focus indicator. `appearance: none` strips the UA
  // ring on some engines, so the 4px blue ring is restored the same inline-handler way the
  // Button does it. Resting appearance is unchanged.
  const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    if (isFocusVisible(e.currentTarget)) {
      e.currentTarget.style.outline = 'var(--lq-focus-ring)';
      e.currentTarget.style.outlineOffset = 'var(--lq-focus-offset, 3px)';
    }
    onFocus?.(e);
  };
  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    e.currentTarget.style.outline = '';
    e.currentTarget.style.outlineOffset = '';
    onBlur?.(e);
  };

  return (
    <label
      htmlFor={boxId}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        fontFamily: 'var(--lq-font-sans)',
        fontSize: 13,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      <span
        data-lq-checkbox=""
        style={{ position: 'relative', width: 22, height: 22, flex: '0 0 22px' }}
      >
        <input
          {...rest}
          id={boxId}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          onChange={onChange}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            position: 'absolute',
            inset: 0,
            margin: 0,
            appearance: 'none',
            WebkitAppearance: 'none',
            width: 22,
            height: 22,
            border: 'var(--lq-border)',
            background: 'var(--lq-white)',
            cursor: 'inherit',
          }}
        />
        <span
          aria-hidden="true"
          data-lq-tick=""
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 950,
            fontSize: 14,
            lineHeight: 1,
            pointerEvents: 'none',
          }}
        >
          ✓
        </span>
        <style>{CHECKBOX_CSS}</style>
      </span>
      {label ? <span>{label}</span> : null}
    </label>
  );
}

export default Checkbox;
